"use client"

import styled from "@emotion/styled"
import { LeadToEmailRequest, postWebContactUsSendToEmail } from "api"
import { useRouter, useSearchParams } from "next/navigation"
import { LegacyRef, Suspense, useEffect, useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { SubmitHandler, useForm } from "react-hook-form"
import { NewText } from "ui"
import { submitError, submitSuccess } from "utils"

const FormField = styled.div`
  margin-bottom: 20px;
`

const StyledTextField = styled.input`
  width: 100%;
  height: 27px;
  line-height: 106.6%;
  font-size: 16px;
  ${(props) => props.theme.breakpoints.up("sm")} {
    height: 30px;
    font-size: 17.6px;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    height: 45px;
    font-size: 26px;
  }

  background-color: transparent;
  border: 1px solid white;
  padding-left: 16px;
  color: white;
  box-sizing: border-box;

  &::placeholder {
    color: white;
  }

  &:focus {
    outline: none;
    border-color: white;
  }
`

const CheckboxContainer = styled.label`
  display: flex;
  align-items: flex-start;
  cursor: pointer;
  align-items: start;
  margin-bottom: 15px;
  ${(props) => props.theme.breakpoints.up("sm")} {
    gap: 5px;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    margin: 20px 0;
  }
`

const Checkbox = styled.input<{ $hasError?: boolean }>`
  width: 9px;
  height: 9px;
  flex-shrink: 0;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: 0.75px solid ${(props) => (props.$hasError ? "#ff0000" : "#ffffff")};
  border-radius: 0;
  background-color: transparent;
  cursor: pointer;
  position: relative;
  box-sizing: border-box;

  /* Force square shape on iOS */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="checkbox"] {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    border-radius: 0 !important;
  }

  &:checked {
    background-color: transparent;
    border-color: ${(props) => (props.$hasError ? "#ff0000" : "#ffffff")};
    border-radius: 0;
  }

  &:checked::after {
    content: "✓";
    position: absolute;
    top: -1px;
    left: 1px;
    color: ${(props) => (props.$hasError ? "#ff0000" : "#ffffff")};
    font-size: 7px;
    font-weight: bold;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px
      ${(props) => (props.$hasError ? "rgba(255, 0, 0, 0.3)" : "rgba(255, 255, 255, 0.3)")};
  }

  ${(props) => props.theme.breakpoints.up("sm")} {
    width: 8px;
    height: 8px;
    border: 0.67px solid ${(props) => (props.$hasError ? "#ff0000" : "#ffffff")};

    &:checked::after {
      font-size: 6px;
      left: 0px;
    }
  }

  ${(props) => props.theme.breakpoints.up("xl")} {
    width: 12px;
    height: 12px;
    border: 1px solid ${(props) => (props.$hasError ? "#ff0000" : "#ffffff")};

    &:checked::after {
      font-size: 9px;
      left: 1px;
    }
  }
`

const CheckboxLabel = styled(NewText)`
  cursor: pointer;
`

const ErrorMessage = styled(NewText)`
  margin-top: 5px;
`

const SubmitButton = styled.button`
  width: 100%;
  height: 45px;
  background-color: black;
  color: white;
  border: none;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #333;
  }

  &:disabled {
    background-color: #666;
    cursor: not-allowed;
  }
`

const SubmitButtonText = styled(NewText)``

const FormTitle = styled(NewText)`
  ${(props) => props.theme.breakpoints.up("sm")} {
    padding: 30px;
    margin-top: 16px;
    padding: 0;
    padding-bottom: 17px;
  }
  margin-top: 25px;
  padding-bottom: 25px;
  ${(props) => props.theme.breakpoints.up("xl")} {
    padding-bottom: 10px;
  }
`

const StyledForm = styled.form`
  padding-bottom: 8px;
  ${(props) => props.theme.breakpoints.up("sm")} {
    padding: 0;
  }
`

const SubmissionSuccess = styled.div`
  width: 100%;
  height: 173px;
  display: flex;
  flex-direction: column;
  text-align: center;
  padding-top: 72px;
  margin-bottom: 72px;
  ${(props) => props.theme.breakpoints.up("sm")} {
    margin-bottom: 0;
    height: 105px;
    padding-top: 25px;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    height: 173px;
    padding-top: 39px;
  }
`

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 26px;
  ${(props) => props.theme.breakpoints.up("xl")} {
    padding: 0 40px;
  }
`

const BottomContainer = styled.div`
  ${(props) => props.theme.breakpoints.down("sm")} {
    .grecaptcha-badge {
      z-index: -1;
      height: 45px !important;
    }
  }
`

const StyledLink = styled.a`
  color: inherit;
`

const StyledDisclaimer = styled.div`
  color: white;
  margin-top: 16px;
  font-size: 12px;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin-top: 50px;
  }
  ${(props) => props.theme.breakpoints.up("xl")} {
    margin-top: 122px;
  }
`

// eslint-disable-next-line prefer-destructuring
const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY
if (!RECAPTCHA_KEY) {
  console.warn("RECAPTCHA_KEY environment variable is not provided.")
}

interface FormData {
  fullName: string
  email: string
  phone: string
  privacyConsent: boolean
}

function SidebarFormInner() {
  const searchParams = useSearchParams()
  const router = useRouter()
  const [submitted, setSubmitted] = useState(false)
  const [submitFailed, setSubmitFailed] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const reCaptchaRef = useRef<ReCAPTCHA | null>()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>()

  // Check for thankyou parameter on component mount
  useEffect(() => {
    const thankyouParam = searchParams.get("thankyou")
    if (thankyouParam === "true") {
      setSubmitted(true)
    }
  }, [searchParams])

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    // Clear previous error state
    setSubmitFailed(false)
    setErrorMessage("")

    if (RECAPTCHA_KEY) {
      reCaptchaRef?.current?.reset()
    }

    let recaptchaResponse = ""
    if (RECAPTCHA_KEY) {
      try {
        recaptchaResponse = (await reCaptchaRef?.current?.executeAsync()) as string
      } catch (error) {
        console.warn("reCAPTCHA execution failed:", error)
      }
    }

    try {
      const senddata: LeadToEmailRequest = {
        projectId: -1, // As requested
        name: data.fullName,
        email: data.email,
        phone: data.phone,
        comments: "",
        gRecaptchaResponse: recaptchaResponse,
        projectStatus: "",
        allowedMarketing: data.privacyConsent || false,
        allowedSmsAndEmail: data.privacyConsent || false,
        companyId: -1, // As requested
        uri: window?.location?.href,
        toEmail: ["alex.fost@mintapp.co.il", "office@shiplus.co.il"],
      }

      const response = await postWebContactUsSendToEmail(senddata)
      if (response?.data?.body?.addLeadSucceeded) {
        submitSuccess("שליחה", "תודה שפניתם אלינו!", true)
        setSubmitted(true)
        reset()

        // Add thankyou parameter to URL
        const url = new URL(window.location.href)
        url.searchParams.set("thankyou", "true")
        router.push(url.pathname + url.search)
      } else {
        setSubmitFailed(true)
        setErrorMessage(response?.data?.body?.errorMessage || "שגיאה בשליחת הטופס")
        submitError("שליחה", "שגיאה בשליחת הטופס", response?.data?.body?.errorMessage)
      }
    } catch (error) {
      setSubmitFailed(true)
      setErrorMessage("שגיאה בשליחת הטופס")
      reset()
      submitError("שליחה", "שגיאה בשליחת הטופס", error as any)
    }
  }

  return (
    <>
      {submitted ? (
        <SubmissionSuccess>
          <NewText
            mobileFontSize={36}
            desktopLineHeight={"100%"}
            desktopFontWeight={700}
            component={"h3"}
            smallDesktopFontSize={22}
            desktopFontSize={36}
            color="white"
          >
            תודה שפניתם אלינו!
          </NewText>
          <NewText
            mobileFontSize={36}
            desktopLineHeight={"100%"}
            desktopFontWeight={200}
            component={"p"}
            smallDesktopFontSize={22}
            desktopFontSize={36}
            color="white"
          >
            נציג שלנו
          </NewText>
          <NewText
            mobileFontSize={36}
            desktopLineHeight={"100%"}
            desktopFontWeight={200}
            smallDesktopFontSize={22}
            desktopFontSize={36}
            component={"p"}
            color="white"
          >
            יחזור אליכם בהקדם
          </NewText>
        </SubmissionSuccess>
      ) : (
        <FormContainer>
          <FormTitle
            component="h2"
            mobileFontSize={27}
            smallDesktopFontSize={18}
            desktopFontSize={27}
            desktopLineHeight={"1"}
            desktopFontWeight={500}
            color="white"
            paddingBottom={0}
          >
            לייעוץ והכוונה, השאירו פרטים ונחזור אליכם בהקדם. השירות ניתן ללא עלות וללא כל התחייבות
            מצידכם.
          </FormTitle>
          <StyledForm onSubmit={handleSubmit(onSubmit)}>
            <CheckboxContainer>
              <Checkbox
                id="privacyConsent"
                type="checkbox"
                $hasError={!!errors.privacyConsent}
                {...register("privacyConsent", {
                  required: "נדרשת הסכמה למדיניות הפרטיות",
                })}
              />
              <CheckboxLabel
                component="span"
                mobileFontSize={9}
                smallDesktopFontSize={11.4}
                desktopFontSize={17}
                desktopLineHeight="106.6%"
                desktopFontWeight={200}
                color="white"
              >
                אני מסכים.ה לקבל משיפוצים פלוס בע&quot;מ ו/או מי מטעמה מידע על הטבות, מבצעים, הנחות
                ו/או דבר פרסומת באמצעים דיגיטליים (דוא&quot;ל, הודעות SMS, חיוג אוטומטי, שיחת טלפון
                ועוד) ובהתאם{" "}
                <StyledLink
                  href="/assets/privacy-policy.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  למדיניות הפרטיות
                </StyledLink>
              </CheckboxLabel>
            </CheckboxContainer>
            {errors.privacyConsent && (
              <ErrorMessage
                component="div"
                mobileFontSize={13}
                smallDesktopFontSize={14}
                desktopFontSize={14}
                mobileLineHeight="1.2em"
                smallDesktopLineHeight="1.2em"
                desktopLineHeight="1.2em"
                desktopFontWeight={400}
                color="#ff0000"
              >
                {errors.privacyConsent.message}
              </ErrorMessage>
            )}
            <FormField>
              <StyledTextField
                {...register("fullName", {
                  required: "שם מלא נדרש",
                })}
                placeholder="שם מלא (שדה חובה)"
                type="text"
              />
              {errors.fullName && (
                <ErrorMessage
                  component="div"
                  mobileFontSize={13}
                  smallDesktopFontSize={14}
                  desktopFontSize={14}
                  mobileLineHeight="1.2em"
                  smallDesktopLineHeight="1.2em"
                  desktopLineHeight="1.2em"
                  desktopFontWeight={400}
                  color="white"
                >
                  {errors.fullName.message}
                </ErrorMessage>
              )}
            </FormField>
            <FormField>
              <StyledTextField
                {...register("email", {
                  required: "אימייל נדרש",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "כתובת אימייל לא תקינה",
                  },
                })}
                placeholder="אימייל (שדה חובה)"
                type="email"
              />
              {errors.email && (
                <ErrorMessage
                  component="div"
                  mobileFontSize={13}
                  smallDesktopFontSize={14}
                  desktopFontSize={14}
                  mobileLineHeight="1.2em"
                  smallDesktopLineHeight="1.2em"
                  desktopLineHeight="1.2em"
                  desktopFontWeight={400}
                  color="white"
                >
                  {errors.email.message}
                </ErrorMessage>
              )}
            </FormField>

            <FormField>
              <StyledTextField
                {...register("phone", {
                  required: "טלפון נייד נדרש",
                })}
                placeholder="טלפון נייד (שדה חובה)"
              />
              {errors.phone && (
                <ErrorMessage
                  component="div"
                  mobileFontSize={13}
                  smallDesktopFontSize={14}
                  desktopFontSize={14}
                  mobileLineHeight="1.2em"
                  smallDesktopLineHeight="1.2em"
                  desktopLineHeight="1.2em"
                  desktopFontWeight={400}
                  color="white"
                >
                  {errors.phone.message}
                </ErrorMessage>
              )}
            </FormField>

            <SubmitButton type="submit" disabled={isSubmitting}>
              <SubmitButtonText
                component="span"
                mobileFontSize={26}
                smallDesktopFontSize={17}
                desktopFontSize={26}
                mobileLineHeight="1.066em"
                smallDesktopLineHeight="1.066em"
                desktopLineHeight="1.066em"
                desktopFontWeight={400}
                color="white"
              >
                {isSubmitting ? "שולח..." : "אשמח לשמוע מכם >>"}
              </SubmitButtonText>
            </SubmitButton>

            {submitFailed && (
              <ErrorMessage
                component="div"
                mobileFontSize={15}
                smallDesktopFontSize={16}
                desktopFontSize={16}
                mobileLineHeight="1.2em"
                smallDesktopLineHeight="1.2em"
                desktopLineHeight="1.2em"
                desktopFontWeight={400}
                color="#ff0000"
                style={{ marginTop: "15px", textAlign: "center" }}
              >
                {errorMessage} - אנא נסו שוב
              </ErrorMessage>
            )}

            <BottomContainer>
              {RECAPTCHA_KEY && (
                <ReCAPTCHA
                  ref={reCaptchaRef as LegacyRef<ReCAPTCHA> | undefined}
                  sitekey={RECAPTCHA_KEY}
                  size="invisible"
                  hl="iw"
                  tabIndex={-1}
                  badge="bottomleft"
                />
              )}
              <StyledDisclaimer>
                *הייעוץ המקצועי ניתן על ידי שיפוצים פלוס בע&quot;מ בלבד. השירותים עצמם מבוצעים על
                ידי אנשי מקצוע עצמאיים (צדדים שלישיים) ובאחריותם הבלעדית. חברת שיפוצים פלוס
                בע&quot;מ ו/או BG BOND ו/או מי מטעמן אינן אחראיות לשירותים שיינתנו, לאיכותם, רמת
                ההתאמה, עמידה בלוחות זמנים, התוצר הסופי ו/או לכל נזק שעלול להיגרם בקשר עימם. בקבלת
                הייעוץ והשירותים, המשתמש מבין כי הדבר נעשה על אחריותו הבלעדית, ומוותר על כל טענה או
                תביעה כלפי שיפוצים פלוס בע&quot;מ ו/או BG BOND בקשר לייעוץ המקצועי והשירותים שיתקבלו
              </StyledDisclaimer>
            </BottomContainer>
          </StyledForm>
        </FormContainer>
      )}
    </>
  )
}

// Loading component for Suspense fallback
const FormLoading = () => (
  <FormContainer>
    <FormTitle
      component="h2"
      mobileFontSize={27}
      smallDesktopFontSize={18}
      desktopFontSize={27}
      desktopLineHeight={"106.6%"}
      desktopFontWeight={500}
      color="white"
    >
      טוען...
    </FormTitle>
  </FormContainer>
)

// Main export component wrapped with Suspense
export default function SidebarForm() {
  return (
    <Suspense fallback={<FormLoading />}>
      <SidebarFormInner />
    </Suspense>
  )
}
